import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone, Inbox } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/bookings`);
        setBookings(res.data);
      } catch (e) {
        setError(e.message || "Failed to load bookings");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div
      data-testid="admin-page"
      className="min-h-screen bg-ink-900 text-white px-6 py-12 md:px-12"
    >
      <div className="max-w-6xl mx-auto">
        <Link
          to="/"
          data-testid="admin-back-link"
          className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-gold hover:text-[#F3E5AB] transition-colors mb-10"
        >
          <ArrowLeft size={14} /> Back to site
        </Link>

        <h1 className="font-serif text-4xl md:text-6xl tracking-tight mb-2">
          Booking <span className="metallic-text italic">Requests</span>
        </h1>
        <p className="text-[#A1A5B5] mb-10">
          Inquiries submitted via the Toliver Signature Services website.
        </p>

        {loading && (
          <p data-testid="admin-loading" className="text-[#A1A5B5]">
            Loading requests…
          </p>
        )}
        {error && (
          <p data-testid="admin-error" className="text-red-400">
            {error}
          </p>
        )}

        {!loading && !error && bookings.length === 0 && (
          <div
            data-testid="admin-empty"
            className="border border-white/10 p-12 text-center"
          >
            <Inbox className="mx-auto mb-4 text-gold" size={32} />
            <p className="text-[#A1A5B5]">
              No booking requests yet. New submissions will appear here.
            </p>
          </div>
        )}

        <div className="space-y-4">
          {bookings.map((b) => (
            <article
              key={b.id}
              data-testid={`admin-booking-${b.id}`}
              className="bg-ink-800 border border-white/5 p-6 md:p-8 hover:border-gold/30 transition-colors"
            >
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="font-serif text-2xl">{b.full_name}</h3>
                  <p className="text-xs uppercase tracking-[0.2em] text-gold mt-1">
                    {b.service}
                    {b.package_tier && ` — ${b.package_tier}`}
                  </p>
                </div>
                <span className="text-xs text-[#64677A]">
                  {new Date(b.created_at).toLocaleString()}
                </span>
              </div>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-[#A1A5B5] mb-4">
                <a
                  href={`mailto:${b.email}`}
                  className="flex items-center gap-2 hover:text-gold"
                >
                  <Mail size={14} /> {b.email}
                </a>
                {b.phone && (
                  <a
                    href={`tel:${b.phone}`}
                    className="flex items-center gap-2 hover:text-gold"
                  >
                    <Phone size={14} /> {b.phone}
                  </a>
                )}
                {b.budget && <span>Budget: {b.budget}</span>}
                {b.timeline && <span>Timeline: {b.timeline}</span>}
              </div>
              <p className="text-white/80 whitespace-pre-wrap border-l-2 border-gold/40 pl-4">
                {b.project_details}
              </p>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
